/**
 * Get today's date in YYYY-MM-DD format using Pacific time.
 * Prevents UTC rollover issues where 4 PM PST shows "tomorrow".
 */
export function getPacificToday(): string {
  return new Date().toLocaleDateString('en-CA', {
    timeZone: 'America/Los_Angeles',
  });
}

/**
 * Parse a YYYY-MM-DD date string as a local date (not UTC).
 *
 * new Date("2026-05-11") parses as UTC midnight, which shifts back
 * one day when displayed in US timezones via toLocaleDateString().
 * This helper constructs the Date in local time so the calendar
 * date is always preserved.
 */
export function parseLocalDate(dateString: string): Date {
  const [y, m, d] = dateString.split('-');
  return new Date(+y, +m - 1, +d);
}

/** Matches a bare date-only value (YYYY-MM-DD) with no time component. */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Format an API date value for display.
 *
 * Date-only strings (YYYY-MM-DD — e.g. a DateField like payment_date or
 * due_date) are parsed as LOCAL dates via parseLocalDate(). Passing them
 * straight to new Date() parses them as UTC midnight, which renders a day
 * early in US timezones through toLocaleDateString() (e.g. "2026-07-24" →
 * "Jul 23"). Full ISO datetime strings carry their own offset and are already
 * correct, so they go to the native parser untouched.
 *
 * Returns '—' for null/empty input.
 */
export function formatDisplayDate(
  dateStr: string | null | undefined,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
  locale: string = 'en-US',
): string {
  if (!dateStr) return '—';
  const date = DATE_ONLY_RE.test(dateStr) ? parseLocalDate(dateStr) : new Date(dateStr);
  return date.toLocaleDateString(locale, options);
}
